import './layout.css';

interface MerktekenProps {
  /** Zijde in pixels. */
  formaat?: number;
  /**
   * Op een donkere ondergrond: de hexagon wordt een witte omtrek in plaats
   * van een gevuld vlak. Zonder dat verdwijnt donkerblauw op donkerblauw.
   */
  diapositief?: boolean;
  className?: string;
}

/**
 * Het merkteken: de druppel ín de hexagon.
 *
 * De hexagon is de vorm van Gebiedsmanagers, de druppel zegt waar deze app
 * over gaat. De highlight in de druppel is het enige detail; die verdwijnt
 * vanzelf onder de 16 px, en dan draagt het silhouet het alleen.
 */
export function Merkteken({
  formaat = 32,
  diapositief = false,
  className,
}: MerktekenProps): JSX.Element {
  return (
    <svg
      className={className}
      width={formaat}
      height={formaat}
      viewBox="0 0 32 32"
      aria-hidden="true"
    >
      <polygon
        points="16,0.8 31.2,8.6 31.2,23.4 16,31.2 0.8,23.4 0.8,8.6"
        fill={diapositief ? 'none' : '#1c2445'}
        stroke={diapositief ? 'rgba(255,255,255,0.9)' : 'none'}
        strokeWidth={diapositief ? 1.2 : 0}
      />
      {/* De druppel houdt aan alle kanten lucht: de krapste speling is 1,6
          van de 32 eenheden. Zonder die marge raakt hij de schuine zijden
          en lijkt het alsof de vorm uitpuilt. */}
      <path
        d="M16 8.6C18 14.2 21.8 17.3 21.8 21.4A5.8 5.8 0 1 1 10.2 21.4C10.2 17.3 14 14.2 16 8.6Z"
        fill="#00afd3"
      />
      <path
        d="M12.7 23.6A4 4 0 0 1 13.6 19.6"
        stroke="#ffffff"
        strokeWidth="1.3"
        strokeLinecap="round"
        fill="none"
        opacity="0.85"
      />
    </svg>
  );
}

interface MerknaamProps {
  diapositief?: boolean;
  formaat?: number;
}

/**
 * Merkteken plus naam, met Gebiedsmanagers eronder.
 *
 * De app heet Afkoppelcoach, maar hij is van Gebiedsmanagers. Dat hoort
 * zichtbaar te zijn waar iemand het merk voor het eerst ziet — op het
 * inlogscherm en in de kopbalk — en niet alleen in een colofon.
 */
export function Merknaam({ diapositief = false, formaat = 36 }: MerknaamProps): JSX.Element {
  return (
    <span className={`merknaam${diapositief ? ' merknaam--dia' : ''}`}>
      <Merkteken formaat={formaat} diapositief={diapositief} />
      <span className="merknaam__tekst">
        <span className="merknaam__app">Afkoppelcoach</span>
        {/* Klein en onder de naam: genoeg om te zien van wie de app is,
            zonder dat het de kopbalk overneemt. */}
        <span className="merknaam__bureau">Gebiedsmanagers</span>
      </span>
    </span>
  );
}

interface BureaulogoProps {
  /** Op een donkere ondergrond het diapositief. */
  diapositief?: boolean;
  hoogte?: number;
}

/**
 * Het logo van Gebiedsmanagers.
 *
 * Het bestand komt uit het designsysteem en staat in public/merk/. Ontbreekt
 * het, dan valt dit terug op de naam in tekst — zichtbaar blijft zichtbaar,
 * ook als het bestand er nog niet is.
 *
 * Het merk wordt met opzet niet nagetekend: die drie gestreepte segmenten
 * hebben vaste hoeken en een vaste streepafstand, en een benadering die er
 * net naast zit is slechter dan tekst.
 */
export function Bureaulogo({ diapositief = false, hoogte = 28 }: BureaulogoProps): JSX.Element {
  const bestand = diapositief ? 'gebiedsmanagers-wit.svg' : 'gebiedsmanagers.svg';

  return (
    <span className={`bureaulogo${diapositief ? ' bureaulogo--dia' : ''}`}>
      <img
        src={`./merk/${bestand}`}
        alt="Gebiedsmanagers"
        height={hoogte}
        style={{ height: hoogte }}
        onError={(event) => {
          // Bestand ontbreekt: de tekst ernaast doet het werk.
          event.currentTarget.style.display = 'none';
          event.currentTarget.parentElement?.classList.add('bureaulogo--tekst');
        }}
      />
      <span className="bureaulogo__terugval">Gebiedsmanagers</span>
    </span>
  );
}
